/**
 * Génère des champs dynamiques à partir des données brutes
 * Calcule les métriques de récurrence, churn, rétention, etc.
 */

import calculateChurnMetrics from './calculateChurnMetrics.js'

const generateDynamicFields = (mockDatabase) => {
  const { accounting_invoices: invoices } = mockDatabase

  // Utiliser la nouvelle logique de calcul du churn
  const { entityMetrics, globalMetrics } = calculateChurnMetrics(invoices)

  // Calculer les ventes et métriques financières pour chaque entité
  const enrichedEntityMetrics = {}

  Object.keys(entityMetrics).forEach(entityId => {
    const entityMetric = entityMetrics[entityId]

    // Calculer le montant total des ventes pour cette entité
    const entityInvoices = invoices.filter(inv => inv.delivery_entity_id === entityId)
    const totalSales = entityInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0)

    // Calculer les ventes moyennes des commandes récurrentes (exclure la première)
    const recurringInvoices = entityInvoices.slice(1) // Toutes sauf la première
    const totalRecurringSales = recurringInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0)
    const avgRecurringSales = recurringInvoices.length > 0 ? totalRecurringSales / recurringInvoices.length : 0

    enrichedEntityMetrics[entityId] = {
      ...entityMetric,
      totalSales,
      avgRecurringSales,
    }
  })

  // Recalculer les métriques globales avec les données financières
  const allEntities = Object.values(enrichedEntityMetrics)
  const activeRecurringBranches = allEntities.filter(e => e.hasRecurringOrders && !e.isChurned).length

  const totalRecurringOrders = allEntities
    .filter(e => !e.isChurned)
    .reduce((sum, e) => sum + e.recurringOrders, 0)

  const avgRecurringOrdersPerBranch = activeRecurringBranches > 0 ? totalRecurringOrders / activeRecurringBranches : 0

  const recurringBranchesWithSales = allEntities.filter(e => e.hasRecurringOrders && !e.isChurned && e.avgRecurringSales > 0)
  const totalAvgRecurringSales = recurringBranchesWithSales.reduce((sum, e) => sum + e.avgRecurringSales, 0)
  const avgSalesRecurringOrders = recurringBranchesWithSales.length > 0 ? totalAvgRecurringSales / recurringBranchesWithSales.length : 0

  const enhancedGlobalMetrics = {
    ...globalMetrics,
    totalRecurringOrders,
    avgRecurringOrdersPerBranch: Math.round(avgRecurringOrdersPerBranch * 100) / 100,
    avgSalesRecurringOrders: Math.round(avgSalesRecurringOrders * 100) / 100,
  }

  // Enrichir les factures avec les champs dynamiques
  const enrichedInvoices = invoices.map(invoice => {
    const entityId = invoice.delivery_entity_id
    const entityMetric = enrichedEntityMetrics[entityId]

    if (!entityMetric) return invoice

    // Trouver la position de cette facture dans l'historique de l'entité
    const orderIndex = entityMetric.orderHistory.findIndex(orderDate => (
      Math.abs(orderDate.getTime() - new Date(invoice.created_at).getTime()) < 1000 // Tolérance de 1 seconde
    ))

    const isFirstOrder = orderIndex === 0
    const orderSequenceNumber = orderIndex + 1

    return {
      ...invoice,
      // Champs dynamiques ajoutés
      is_first_order: isFirstOrder,
      order_sequence_number: orderSequenceNumber,
      is_recurring_order: !isFirstOrder,
      entity_has_recurring_orders: entityMetric.hasRecurringOrders,
      entity_is_churned: entityMetric.isChurned,
      entity_retention_status: entityMetric.hasRecurringOrders ? 'recurring' : 'one_time',
      days_since_entity_first_order: entityMetric.daysSinceFirstOrder,
      days_since_entity_last_order: entityMetric.daysSinceLastOrder,
      entity_total_orders: entityMetric.totalOrders,
      entity_recurring_orders: entityMetric.recurringOrders,
      entity_avg_recurring_sales: entityMetric.avgRecurringSales,
      entity_avg_interval_days: Math.round(entityMetric.avgIntervalDays),
    }
  })

  return {
    enrichedInvoices,
    entityMetrics: enrichedEntityMetrics,
    globalMetrics: enhancedGlobalMetrics,
  }
}

export default generateDynamicFields